import { trackGtagEvent } from '@/utils/Analytics';
import { isInternalTraffic } from '@/utils/internalTraffic';

/**
 * Post-capture handoff on /try. A: the results screen. B and C go straight into
 * day 1 and differ in how the results show: B a card above question 1, C a sheet
 * over question 1. B and C also get the "Pass Plan · Day N of 7" pill.
 * Prod is always B. On dev/staging `?handoff=A|B|C` sets it and localStorage keeps
 * it across navigation; unset or anything else (a retired D) is B.
 */
export type HandoffVariant = 'A' | 'B' | 'C';

const KEY = 'swtpa.handoffVariant';
const FIRST_ANSWER_KEY = 'swtpa.handoffFirstAnswerPending';
const HANDOFF_SHEET_SEEN_KEY = 'swtpa.handoffSheetSeen';
const RESULTS_KEY = 'swtpa.handoffResults';

/** Dev server or the i9 staging build (`build:staging` bakes base /staging/web/). */
const isHandoffSwitchLive = (): boolean =>
  import.meta.env.DEV || import.meta.env.BASE_URL.startsWith('/staging/');

const isVariant = (v: unknown): v is HandoffVariant => v === 'A' || v === 'B' || v === 'C';

// Rollback to the results screen for everyone: return 'A' here.
const DEFAULT_VARIANT: HandoffVariant = 'B';

export function handoffVariant(): HandoffVariant {
  if (!isHandoffSwitchLive() || typeof window === 'undefined') return DEFAULT_VARIANT;
  const forced = new URLSearchParams(window.location.search).get('handoff')?.toUpperCase();
  try {
    if (isVariant(forced)) localStorage.setItem(KEY, forced);
    const stored = localStorage.getItem(KEY);
    return isVariant(stored) ? stored : DEFAULT_VARIANT;
  } catch {
    return isVariant(forced) ? forced : DEFAULT_VARIANT; // storage blocked: the URL still picks
  }
}

/** Arms the one-shot first-answer event with the experience actually shown; the quiz fires it on the next answer. */
export function armHandoffFirstAnswer(shown: HandoffVariant): void {
  try {
    localStorage.setItem(FIRST_ANSWER_KEY, shown);
  } catch {
    // Storage blocked: the first-answer event is lost, the flow is not.
  }
}

export function trackHandoffFirstAnswer(): void {
  let shown: string | null;
  try {
    shown = localStorage.getItem(FIRST_ANSWER_KEY);
    if (!shown) return;
    localStorage.removeItem(FIRST_ANSWER_KEY);
  } catch {
    return;
  }
  if (isInternalTraffic()) return; // same suppression as every /try event
  // '1' is an arm from before the shown variant was stored.
  trackGtagEvent('handoff_first_answer', { handoff_variant: isVariant(shown) ? shown : handoffVariant() });
}

export function trackHandoffResultsOpened(): void {
  if (isInternalTraffic()) return;
  trackGtagEvent('handoff_results_opened', { handoff_variant: handoffVariant() });
}

/** The aim screen's first-question path: /start opens today's trial module (weakest first). */
export const handoffStartPath = (exam?: string | null): string =>
  `/start?${exam ? `type=${exam.toLowerCase()}&` : ''}questions=5&src=aim_first_question`;

export interface HandoffResults {
  uid: string;
  correct: number;
  total: number;
  weak: string[];
  exam: string | null;
}

/**
 * This capture's results for the quiz card (B) and sheet (C).
 * diagnosticMemory skips internal traffic and the capture account has no
 * profile.diagnostic, so without this they had nothing to show. Saving clears the
 * seen flag so each capture shows once; the uid keeps a second account on the
 * same browser from reading it.
 */
export function saveHandoffResults(results: HandoffResults): void {
  try {
    localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
    localStorage.removeItem(HANDOFF_SHEET_SEEN_KEY);
  } catch {
    // Storage blocked: the sheet falls back to diagnostic memory.
  }
}

export function readHandoffResults(uid: string | null | undefined): HandoffResults | null {
  if (!uid) return null;
  try {
    const v = JSON.parse(localStorage.getItem(RESULTS_KEY) ?? 'null') as Partial<HandoffResults> | null;
    if (!v || v.uid !== uid || !Array.isArray(v.weak) || !v.weak.every((w) => typeof w === 'string')) return null;
    if (typeof v.correct !== 'number' || typeof v.total !== 'number') return null;
    return { uid, correct: v.correct, total: v.total, weak: v.weak, exam: typeof v.exam === 'string' ? v.exam : null };
  } catch {
    return null;
  }
}

export function readHandoffSeen(): boolean {
  try {
    return localStorage.getItem(HANDOFF_SHEET_SEEN_KEY) === '1';
  } catch {
    return true; // storage blocked: never trap them under a sheet that reopens every visit
  }
}

export function markHandoffSeen(): void {
  try {
    localStorage.setItem(HANDOFF_SHEET_SEEN_KEY, '1');
  } catch {
    // Storage blocked: it shows again next time.
  }
}
